import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  firebaseSignInWithCustomToken, firebaseSignOut, getFirebaseAuth, subscribeAuth,
} from "./firebase";

export type AuthState = {
  status: "loading" | "signedOut" | "signedIn";
  user: { uid: string } | null;
  getIdToken(force?: boolean): Promise<string>;
  signInWithToken(customToken: string): Promise<void>;
  signOut(): Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<{ status: AuthState["status"]; uid: string | null }>({
    status: "loading", uid: null,
  });

  useEffect(
    () => subscribeAuth((u) => setSnapshot({ status: u ? "signedIn" : "signedOut", uid: u?.uid ?? null })),
    [],
  );

  const value = useMemo<AuthState>(() => ({
    status: snapshot.status,
    user: snapshot.uid ? { uid: snapshot.uid } : null,
    async getIdToken(force?: boolean) {
      const current = getFirebaseAuth().currentUser;
      if (!current) throw new Error("Cannot get an ID token while signed out.");
      return current.getIdToken(force);
    },
    async signInWithToken(customToken: string) {
      await firebaseSignInWithCustomToken(customToken);
    },
    async signOut() {
      await firebaseSignOut();
    },
  }), [snapshot]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
